import { useState } from "react";
import { format, parse, isValid } from "date-fns";
import { de } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Einheitlicher deutscher Datums-/Datum-Uhrzeit-Picker.
// value/onChange-Vertrag identisch zu den nativen Feldern:
//   - Datum:        "YYYY-MM-DD"
//   - Datum+Zeit:   "YYYY-MM-DDTHH:mm"  (wie datetime-local)
const triggerBase = "flex items-center justify-between gap-2 w-full bg-[#1A1A1A] border border-white/10 text-white text-sm focus:border-[#ff0033] outline-none";

const minuteOptions = (current) => {
  const set = new Set();
  for (let m = 0; m < 60; m += 5) set.add(String(m).padStart(2, "0"));
  if (current) set.add(current);
  return [...set].sort();
};

export function GermanDatePicker({ value, onChange, showTime = false, placeholder = "Datum wählen", className = "p-3", testId }) {
  const [open, setOpen] = useState(false);
  const datePart = value ? value.slice(0, 10) : "";
  const timePart = showTime ? (value && value.length >= 16 ? value.slice(11, 16) : "10:00") : "";
  const hh = timePart ? timePart.slice(0, 2) : "10";
  const mm = timePart ? timePart.slice(3, 5) : "00";
  const parsed = datePart ? parse(datePart, "yyyy-MM-dd", new Date()) : undefined;
  const selected = parsed && isValid(parsed) ? parsed : undefined;

  const emit = (d, t) => {
    if (!d) { onChange(""); return; }
    const ds = format(d, "yyyy-MM-dd");
    onChange(showTime ? `${ds}T${t || timePart || "10:00"}` : ds);
  };

  const label = selected
    ? format(selected, "dd.MM.yyyy", { locale: de }) + (showTime ? ` · ${hh}:${mm} Uhr` : "")
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} className={`${triggerBase} ${className}`}>
          <span className={selected ? "text-white" : "text-neutral-500"}>{label}</span>
          <CalendarIcon size={15} className="text-neutral-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0 bg-[#0F0F0F] border border-white/10 text-white">
        <Calendar
          mode="single"
          locale={de}
          selected={selected}
          defaultMonth={selected}
          onSelect={(d) => { emit(d, `${hh}:${mm}`); if (!showTime) setOpen(false); }}
          classNames={{
            caption_label: "text-sm font-medium text-white capitalize",
            head_cell: "text-neutral-500 rounded-md w-8 font-normal text-[0.8rem]",
            day: "h-8 w-8 p-0 font-normal text-neutral-200 hover:bg-white/10 rounded-md aria-selected:opacity-100",
            day_selected: "bg-[#ff0033] text-white hover:bg-[#ff0033] focus:bg-[#ff0033]",
            day_today: "border border-[#ff0033]/60 text-white",
            day_outside: "text-neutral-600",
            day_disabled: "text-neutral-700 opacity-50",
          }}
        />
        {showTime && (
          <div className="flex items-center gap-2 border-t border-white/10 p-3">
            <span className="text-neutral-500 text-xs uppercase tracking-widest font-mono-lab">Uhrzeit</span>
            <select value={hh} onChange={(e) => emit(selected || new Date(), `${e.target.value}:${mm}`)} className="bg-[#1A1A1A] border border-white/10 text-white p-1.5 text-sm outline-none focus:border-[#ff0033]" data-testid={testId ? `${testId}-hour` : undefined}>
              {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")).map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
            <span className="text-white">:</span>
            <select value={mm} onChange={(e) => emit(selected || new Date(), `${hh}:${e.target.value}`)} className="bg-[#1A1A1A] border border-white/10 text-white p-1.5 text-sm outline-none focus:border-[#ff0033]">
              {minuteOptions(mm).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <span className="text-neutral-500 text-xs">Uhr</span>
            <button type="button" onClick={() => setOpen(false)} className="ml-auto bg-[#ff0033] text-white font-mono-lab text-[10px] uppercase tracking-widest px-3 py-1.5">OK</button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
